/**
 * The rules for the What's New pop-up (components/welcome/WhatsNewNotice): which release note it
 * shows, and when. Pure, so the rules are unit-tested without mounting the layout.
 */
import { PREF_KEYS } from '../hooks/useUserPreferences';
import { tourMayOpenHere, tourSeen, type WelcomeTourRecord, type WelcomeTourRole } from '../components/welcome/welcomeTourModel';
import type { ReleaseNote } from './releaseNotes';

/** useUserPreferences key. The value is the id of the newest note this user has seen (or been spared). */
export const WHATS_NEW_SEEN_PREF = PREF_KEYS.WHATS_NEW_SEEN;

const ID_PATTERN = /^(\d{4}-\d{2}-\d{2})(?:-([b-z]))?$/;

/** Whether `id` is 'YYYY-MM-DD' or 'YYYY-MM-DD-b' (to '-z'). */
export function isValidNoteId(id: string): boolean {
  return ID_PATTERN.test(id);
}

/**
 * Orders two note ids: by date, then the same day's plain id before '-b', '-b' before '-c'.
 * Negative when `a` is older. An id that does not fit the pattern sorts before every valid one.
 */
export function compareNoteIds(a: string, b: string): number {
  const ma = ID_PATTERN.exec(a);
  const mb = ID_PATTERN.exec(b);
  if (!ma || !mb) return (ma ? 1 : 0) - (mb ? 1 : 0);
  if (ma[1] !== mb[1]) return ma[1] < mb[1] ? -1 : 1;
  const sa = ma[2] ?? '';
  const sb = mb[2] ?? '';
  if (sa === sb) return 0;
  return sa < sb ? -1 : 1;
}

/** The newest note flagged `announce` that is newer than `seen` (any announced note when nothing is seen). */
export function newestUnseenAnnouncement(notes: ReleaseNote[], seen: string | null | undefined): ReleaseNote | null {
  let best: ReleaseNote | null = null;
  for (const note of notes) {
    if (!note.announce) continue;
    if (seen && compareNoteIds(note.id, seen) <= 0) continue;
    if (!best || compareNoteIds(note.id, best.id) > 0) best = note;
  }
  return best;
}

/** The newest note id of all (announced or not) dated on or before `onOrBefore` ('YYYY-MM-DD'); all of them when left out. */
export function newestNoteId(notes: ReleaseNote[], onOrBefore?: string): string | null {
  let best: string | null = null;
  for (const note of notes) {
    if (onOrBefore && note.date > onOrBefore) continue;
    if (!best || compareNoteIds(note.id, best) > 0) best = note.id;
  }
  return best;
}

/** 'YYYY-MM-DD' of an ISO time, in local time. Null when it does not parse. */
export function localDay(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export type WhatsNewDecision =
  /** Nothing to do. */
  | { kind: 'none' }
  /** Store this id as seen and show nothing: the user starts from here. */
  | { kind: 'baseline'; id: string }
  /** Show this note. */
  | { kind: 'show'; note: ReleaseNote };

/**
 * What the pop-up does now.
 *
 *  - Never on a screen or for a user the welcome tour never opens for (touch screens, Selective
 *    Touch Direct Load employees; tablets, bedside and kitchen are outside the main layout).
 *  - Never while the welcome tour is open or paused, nor before this user has seen it: the tour
 *    comes first.
 *  - A user with nothing stored yet (just finished the tour, or the first sign-in since this
 *    started) starts from the notes that existed when they took the tour, so someone new never gets
 *    the tour and then an old notice. Without a tour date, from the newest note.
 *  - Otherwise the newest announced note newer than the stored id, if there is one.
 */
export function decideWhatsNew(args: {
  notes: ReleaseNote[];
  seen: string | null | undefined;
  pathname: string;
  role: WelcomeTourRole | null | undefined;
  tourRecord: WelcomeTourRecord | null | undefined;
  tourActive: boolean;
}): WhatsNewDecision {
  if (!args.role) return { kind: 'none' };
  if (!tourMayOpenHere({ pathname: args.pathname, role: args.role })) return { kind: 'none' };
  if (args.tourActive || !tourSeen(args.tourRecord)) return { kind: 'none' };

  if (!args.seen) {
    const tourDay = localDay(args.tourRecord?.shownAt);
    const id = (tourDay && newestNoteId(args.notes, tourDay)) || newestNoteId(args.notes);
    if (!id) return { kind: 'none' };
    // A note announced after they took the tour (they came back days later) still shows.
    const later = newestUnseenAnnouncement(args.notes, id);
    return later ? { kind: 'show', note: later } : { kind: 'baseline', id };
  }

  const note = newestUnseenAnnouncement(args.notes, args.seen);
  return note ? { kind: 'show', note } : { kind: 'none' };
}

/** 'YYYY-MM-DD' as words: "October 29, 2026". */
export function formatNoteDate(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}
